import * as React from "react";
import {
  GridColDef,
  GridRenderCellParams,
  GridRowsProp,
  GridSortModel,
  GridRowModel,
  GridToolbarContainer,
  GridToolbarExport,
  GridColumnVisibilityModel,
} from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import {
  Box,
  Button,
  IconButton,
  LinearProgress,
  Link,
  Stack,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  GlassDataGrid,
  StyledTextField,
  CardBase,
} from "../../components/global";
import ConfirmDeleteModal from "../../components/ConfirmDeleteModal";
import NoResultsOverlay from "../../components/NoResultOverlay";
import { toast } from "react-toastify";
import useDebounce from "../../hooks/useDebounce";
import { useTitle } from "../../hooks/useTitle";
import { useUILabel } from "../../hooks/useUILabel";
import { Location } from "../../types/location";
import { useNavigate } from "react-router-dom";
import LocationModalForm from "./LocationModalForm";
import LocationDrawer from "./LocationDrawer";
import {
  useDeleteLocationMutation,
  useGetLocationsQuery,
} from "../../services/location.service";

const TableToolbar = ({
  onSearch,
  onCreate,
}: {
  onSearch: (query?: string) => void;
  onCreate: () => void;
}) => {
  const addLocationLabel = useUILabel("location.addLocation");
  const enterToSearchLabel = useUILabel("common.enterToSearch");
  const resetLabel = useUILabel("common.reset");
  const locationsLabel = useUILabel("common.locations");
  const addLabel = useUILabel("common.add");
  const [query, setQuery] = React.useState<string | undefined>(undefined);
  const debounceQuery = useDebounce(query, 500);

  React.useEffect(() => {
    onSearch(debounceQuery);
  }, [debounceQuery]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <GridToolbarContainer
      sx={{
        px: { xs: 0.5, sm: 1, md: 1.25 },
        py: { xs: 0.5, sm: 0.75, md: 1 },
        mb: { xs: 0.5, sm: 0.75, md: 1 },
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: 1, sm: 1.5, md: 2 },
      }}
    >
      <StyledTextField
        margin="dense"
        label={enterToSearchLabel}
        value={query || ""}
        onChange={(e) => setQuery(e.target.value)}
        size="small"
        sx={{
          minWidth: { xs: "100%", sm: 200, md: 250 },
          fontSize: { xs: "0.875rem", sm: "1rem" },
        }}
      />
      {!!query && (
        <Button
          variant="text"
          size="small"
          sx={{
            mt: { xs: 0, sm: 1 },
            fontSize: { xs: "0.875rem", sm: "0.9375rem" },
            alignSelf: { xs: "flex-start", sm: "auto" },
          }}
          onClick={() => setQuery("")}
        >
          {resetLabel}
        </Button>
      )}
      <Stack
        sx={{ flex: 1, width: { xs: "100%", sm: "auto" } }}
        direction="row"
        justifyContent={{ xs: "space-between", sm: "flex-end" }}
        alignItems="center"
        gap={{ xs: 1, sm: 1.5, md: 2 }}
      >
        <GridToolbarExport
          printOptions={{ disableToolbarButton: true }}
          csvOptions={{ allColumns: true, fileName: locationsLabel }}
        />
        <Button
          variant="contained"
          onClick={onCreate}
          size="small"
          startIcon={<AddIcon />}
          sx={{
            ml: { xs: 0, sm: 2 },
            fontSize: { xs: "0.875rem", sm: "0.9375rem", md: "1rem" },
            px: { xs: 1.5, sm: 2, md: 2.5 },
            py: { xs: 0.75, sm: 1 },
          }}
        >
          {isMobile ? addLabel : addLocationLabel}
        </Button>
      </Stack>
    </GridToolbarContainer>
  );
};

const LocationsPage = () => {
  const locationsManagementLabel = useUILabel("location.locationsManagement");
  const locationsLabel = useUILabel("common.locations");
  const locationNameLabel = useUILabel("location.locationName");
  const siteNumberLabel = useUILabel("common.siteNumber");
  const addLocationLabel = useUILabel("location.addLocation");
  const addressLabel = useUILabel("common.address");
  const editLabel = useUILabel("common.edit");
  const deleteLabel = useUILabel("common.delete");
  const idLabel = useUILabel("common.id");
  const contactNameLabel = useUILabel("common.contactName");
  const phonenumberLabel = useUILabel("common.phonenumber");
  const cityLabel = useUILabel("common.city");
  const stateLabel = useUILabel("common.state");
  const zipLabel = useUILabel("common.zip");
  const successfullyDeletedLabel = useUILabel("common.successfullyDeleted");
  const deleteLocationQuestionLabel = useUILabel("location.deleteLocationQuestion");
  useTitle(locationsManagementLabel);
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobileView = useMediaQuery(theme.breakpoints.down("sm"));
  const isTabletView = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const [deleteModal, setDeleteModal] = React.useState<{
    open: boolean;
    row: GridRowModel<Location> | null;
  }>({
    open: false,
    row: null,
  });
  const [formModal, setFormModal] = React.useState<{
    open: boolean;
    data: Location | null;
  }>({
    open: false,
    data: null,
  });
  const [page, setPage] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(100);
  const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
  const [searchQuery, setSearchQuery] = React.useState<string | undefined>(
    undefined
  );
  const [open, setOpen] = React.useState(false);
  const [currentItem, setCurrentItem] = React.useState<Location | null>(null);

  const { data, isLoading, isFetching } = useGetLocationsQuery({
    page,
    pageSize,
    sortField: sortModel.length > 0 ? sortModel[0].field : undefined,
    sortMode: sortModel.length > 0 ? sortModel[0].sort : undefined,
    search: searchQuery,
  });
  const rowCount = React.useMemo(() => data?.meta.total, [data]);

  const [doDelete, { isLoading: deleting }] = useDeleteLocationMutation();

  const getColumnVisibilityModel = React.useCallback(
    (): GridColumnVisibilityModel => ({
      id: !isMobileView,
      first_name: true,
      name: false,
      site_number: !isMobileView,
      primary_contact_name: true,
      primary_contact_phone_number: !isMobileView && !isTabletView,
      address_1: false,
      city: true,
      state: true,
      zip: false,
      control: true,
    }),
    [isMobileView, isTabletView]
  );

  const [columnVisibilityModel, setColumnVisibilityModel] =
    React.useState<GridColumnVisibilityModel>(() => getColumnVisibilityModel());

  React.useEffect(() => {
    setColumnVisibilityModel(getColumnVisibilityModel());
  }, [getColumnVisibilityModel]);

  React.useEffect(() => {
    setPageSize((prev) => {
      const targetSize = isMobileView ? 50 : 100;
      return prev === targetSize ? prev : targetSize;
    });
  }, [isMobileView]);

  const rowsPerPageOptions = React.useMemo(
    () => (isMobileView ? [25, 50] : [50, 100]),
    [isMobileView]
  );

  const gridHeight = React.useMemo(() => {
    if (isMobileView) return "calc(100vh - 260px)";
    if (isTabletView) return "75vh";
    return "85vh";
  }, [isMobileView, isTabletView]);

  const handleDeleteRow = React.useCallback((row: GridRowModel<Location>) => {
    setDeleteModal({ open: true, row });
  }, []);
  const handleEditRow = React.useCallback((row: GridRowModel<Location>) => {
    setFormModal({ open: true, data: row });
  }, []);

  const rows: GridRowsProp | undefined = React.useMemo(
    () => data?.data,
    [data]
  );

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: "id",
        headerName: idLabel,
        hide: false,
      },
      {
        field: "first_name",
        headerName: locationNameLabel,
        flex: 1,
        minWidth: 150,
        sortingOrder: ["desc", "asc", null],
        filterable: true,
        renderCell: (params: GridRenderCellParams) => (
          <Link
            component="button"
            variant="body2"
            onClick={() => {
              setCurrentItem(params.row);
              setOpen(true);
            }}
          >
            {params.row.name}
          </Link>
        ),
        disableExport: true,
      },
      {
        field: "name",
        headerName: locationNameLabel,
        hide: true,
      },
      {
        field: "site_number",
        headerName: siteNumberLabel,
        minWidth: 250,
        hide: false,
      },
      {
        field: "primary_contact_name",
        headerName: contactNameLabel,
        headerAlign: "center",
        minWidth: 200,
        sortable: true,
        filterable: true,
        align: "center",
      },
      {
        field: "primary_contact_phone_number",
        headerName: phonenumberLabel,
        hide: true,
      },
      {
        field: "address_1",
        headerName: addressLabel,
        hide: true,
      },
      {
        field: "city",
        headerName: cityLabel,
        headerAlign: "center",
        minWidth: 150,
        sortable: true,
        filterable: true,
        align: "center",
      },
      {
        field: "state",
        headerName: stateLabel,
        headerAlign: "center",
        minWidth: 140,
        sortable: true,
        filterable: true,
        align: "center",
      },
      {
        field: "zip",
        headerName: zipLabel,
        hide: true,
      },
      {
        field: "control",
        headerName: `${editLabel}/${deleteLabel}`,
        headerAlign: "center",
        minWidth: 140,
        sortable: false,
        filterable: false,
        align: "center",
        renderCell: (params: GridRenderCellParams) => (
          <Stack direction={"row"}>
            <IconButton onClick={() => handleEditRow(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={() => handleDeleteRow(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ),
        disableExport: true,
      },
    ],
    [handleEditRow, handleDeleteRow, idLabel, locationNameLabel, siteNumberLabel, contactNameLabel, phonenumberLabel, addressLabel, cityLabel, stateLabel, zipLabel, editLabel, deleteLabel]
  );

  const handleSearch = React.useCallback((query?: string) => {
    setSearchQuery(query);
  }, []);

  const handleCreate = React.useCallback(() => {
    setFormModal({ open: true, data: null });
    // navigate(`/locations/create`);
  }, []);

  const closeDeleteModal = React.useCallback(
    () => setDeleteModal((prev) => ({ ...prev, open: false })),
    []
  );

  const handleDelete = React.useCallback(async () => {
    if (deleteModal.row?.id) {
      try {
        await doDelete(deleteModal.row.id).unwrap();
        closeDeleteModal();
        toast.success(successfullyDeletedLabel);
      } catch (err) {
        closeDeleteModal();
      }
    }
  }, [closeDeleteModal, deleteModal.row?.id, doDelete]);

  const CustomToolbar = React.useMemo(
    () => () => <TableToolbar onSearch={handleSearch} onCreate={handleCreate} />,
    [handleSearch, handleCreate]
  );

  return (
    <Box sx={{ width: "auto", px: 0, py: { xs: 1.5, sm: 2, md: 3 } }}>
      <CardBase
        title={locationsLabel}
        useGlassCard={true}
        nested={false}
        headerActions={
          <Button
            variant="contained"
            color="success"
            onClick={handleCreate}
            startIcon={<AddIcon />}
            size="small"
            sx={{
              textTransform: "none",
              fontWeight: 600,
              fontSize: { xs: "0.875rem", sm: "0.9375rem", md: "1rem" },
              px: { xs: 1.5, sm: 2, md: 2.5 },
              py: { xs: 0.75, sm: 1 },
            }}
          >
            {addLocationLabel}
          </Button>
        }
        sx={{
          "& .card-base__header": {
            flexDirection: "row !important",
            alignItems: "center !important",
            justifyContent: "space-between",
            gap: { xs: 1, sm: 1.5, md: 2 },
            flexWrap: { xs: "wrap", sm: "nowrap" },
            "& > div:first-of-type": {
              flex: 1,
              minWidth: 0,
            },
            "& > div:last-of-type": {
              flexShrink: 0,
              width: "auto",
            },
          },
        }}
      >
        <GlassDataGrid
          height={gridHeight}
          paperSx={{
            overflow: isMobileView ? "visible" : "hidden",
          }}
          rows={rows || []}
          columns={columns}
          paginationMode="server"
          rowCount={rowCount}
          page={page as any}
          onPageChange={(newPage: any) => setPage(newPage)}
          pageSize={pageSize as any}
          rowsPerPageOptions={rowsPerPageOptions}
          onPageSizeChange={(newSize: any) => setPageSize(newSize)}
          sortModel={sortModel}
          sortingMode="server"
          onSortModelChange={(newSortModel: GridSortModel) =>
            setSortModel(newSortModel)
          }
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={(model: GridColumnVisibilityModel) =>
            setColumnVisibilityModel(model)
          }
          loading={isLoading || isFetching}
          components={{
            LoadingOverlay: LinearProgress,
            NoResultsOverlay: NoResultsOverlay,
            Toolbar: CustomToolbar,
          }}
          disableSelectionOnClick
          disableColumnFilter
          density="compact"
          sx={{
            "& .MuiDataGrid-columnHeaders": {
              backgroundColor: isDark
                ? "rgba(255, 255, 255, 0.08)"
                : "rgba(255, 255, 255, 0.5)",
              backdropFilter: "blur(15px) saturate(180%)",
              WebkitBackdropFilter: "blur(15px) saturate(180%)",
              borderBottom: `2px solid ${isDark
                ? "rgba(255, 255, 255, 0.15)"
                : "rgba(46, 125, 60, 0.3)"
                }`,
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
                fontSize: "0.875rem",
                textTransform: "uppercase",
                letterSpacing: "0.8px",
                color: "#12903C",
              },
            },
            "& .MuiDataGrid-row": {
              minHeight: isMobileView ? 46 : 40,
            },
            "& .MuiDataGrid-cell": {
              py: isMobileView ? 1 : 0.75,
              fontSize: isMobileView ? "0.8rem" : "0.875rem",
              lineHeight: 1.4,
            },
            "& .MuiDataGrid-virtualScrollerContent": {
              minWidth: isMobileView ? 520 : "auto",
            },
            "& .MuiDataGrid-toolbarContainer": {
              flexDirection: { xs: "column", sm: "row" },
              alignItems: { xs: "stretch", sm: "center" },
              gap: { xs: 1, sm: 2 },
            },
          }}
        />
      </CardBase>
      <ConfirmDeleteModal
        open={deleteModal.open}
        onClose={closeDeleteModal}
        onDelete={handleDelete}
        message={deleteLocationQuestionLabel.replace("{name}", deleteModal.row?.name || "")}
      />
      <LocationModalForm
        open={formModal.open}
        onClose={() => setFormModal({ open: false, data: null })}
        data={formModal.data}
      />
      <LocationDrawer data={currentItem!} open={open} setOpen={setOpen} />
    </Box>
  );
};
export default LocationsPage;
